import React, { useState, useEffect } from 'react';
import { ArrowRight, Pause, ChevronDown, Settings } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface TranscriptLine {
  id: number;
  original: string;
  translated: string;
}

export const SeeInAction: React.FC = () => {
  const { t } = useTranslation();
  const [activeTranscriptIndex, setActiveTranscriptIndex] = useState(0);
  const [masterTranscripts, setMasterTranscripts] = useState<TranscriptLine[]>([]);
  const [listenerTranscripts, setListenerTranscripts] = useState<TranscriptLine[]>([]);
  const [animatingId, setAnimatingId] = useState<number | null>(null);

  const sampleTranscripts: TranscriptLine[] = [
    {
      id: 1,
      original: 'Welcome to our service today.',
      translated: 'Willkommen zu unserem Gottesdienst heute.'
    },
    {
      id: 2,
      original: 'Let us begin with a prayer.',
      translated: 'Lasst uns mit einem Gebet beginnen.'
    },
    {
      id: 3,
      original: 'Today we will explore faith and hope.',
      translated: 'Heute werden wir Glauben und Hoffnung erkunden.'
    },
    {
      id: 4,
      original: 'Thank you for joining us.',
      translated: 'Vielen Dank, dass Sie bei uns sind.'
    }
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      if (activeTranscriptIndex < sampleTranscripts.length) {
        const currentTranscript = sampleTranscripts[activeTranscriptIndex];
        
        // Add to master page
        setMasterTranscripts(prev => [...prev, currentTranscript]);
        setAnimatingId(currentTranscript.id);
        
        // After animation delay, add to listener page
        setTimeout(() => {
          setListenerTranscripts(prev => [...prev, currentTranscript]);
          setAnimatingId(null);
        }, 800);
        
        setActiveTranscriptIndex(prev => prev + 1);
      } else {
        // Reset and restart
        setTimeout(() => {
          setMasterTranscripts([]);
          setListenerTranscripts([]);
          setActiveTranscriptIndex(0);
        }, 2000);
      }
    }, 2500);

    return () => clearInterval(interval);
  }, [activeTranscriptIndex]);

  return (
    <section id="see-in-action" className="py-16 md:py-24 bg-gradient-to-b from-white to-gray-50">
      <div className="container mx-auto px-2 sm:px-4 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-16 px-2 sm:px-2">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            {t('seeInAction.title')}
          </h2>
          <p className="text-xl text-gray-600">
            {t('seeInAction.info')}
          </p>
        </div>

        <div className="max-w-3xl mx-auto">
          <div className="relative">
            
            {/* Master Session Page - Full Width */}
            <div className="relative">
              <div className="bg-gray-50 rounded-lg shadow-md overflow-hidden border border-gray-200">
                {/* Header Bar - Like SessionPage */}
                <div className="bg-white shadow-sm p-2 sm:p-3 lg:p-4">
                  <div className="flex justify-between items-center gap-2">
                    <div className="flex items-center space-x-2 sm:space-x-4 min-w-0">
                      <h2 className="text-sm sm:text-base lg:text-xl font-bold text-gray-900 truncate">Demo</h2>
                      <div className="flex items-center bg-gray-100 px-2 sm:px-3 py-0.5 sm:py-1 rounded-full flex-shrink-0">
                        <span className="text-[9px] sm:text-xs lg:text-sm font-medium text-gray-700">🇬🇧 <span className="hidden sm:inline">English</span><span className="sm:hidden">English</span></span>
                      </div>
                    </div>
                    <div className="flex space-x-1.5 sm:space-x-2 lg:space-x-4 items-center flex-shrink-0">
                      {/* Settings Button */}
                      <button 
                        type="button"
                        className="p-1.5 sm:p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-full transition-colors"
                        title="Settings"
                      >
                        <Settings className="h-3.5 w-3.5 sm:h-4 sm:w-4 lg:h-5 lg:w-5" />
                      </button>
                      
                      {/* Pause Session Button - Red */}
                      <button className="flex items-center justify-center px-2 sm:px-3 lg:px-4 py-1 sm:py-1.5 lg:py-2 rounded-lg transition-colors bg-red-100 text-red-700 hover:bg-red-200">
                        <Pause className="h-3.5 w-3.5 sm:h-4 sm:w-4 lg:h-5 lg:w-5 mr-1 sm:mr-1.5 lg:mr-2" />
                        <span className="text-[9px] sm:text-xs lg:text-sm">Stop session</span>
                      </button>
                    </div>
                  </div>
                </div>

                {/* Stenographer Area - Like the real Stenographer component */}
                <div className="bg-gray-50 p-2 sm:p-3 lg:p-4 overflow-y-auto h-[300px] sm:h-[300px] lg:h-[340px]">
                  {masterTranscripts.length === 0 ? (
                    <div className="flex items-start justify-center h-full pt-4">
                      <p className="text-gray-500 text-[10px] sm:text-sm lg:text-base text-center px-2">Start the session to begin translation</p>
                    </div>
                  ) : (
                    <div className="space-y-1.5 sm:space-y-2">
                      {masterTranscripts.map((transcript) => (
                        <div
                          key={transcript.id}
                          className="bg-white p-2 sm:p-3 lg:p-4 rounded-lg shadow-sm animate-fadeIn"
                        >
                          <p className="text-gray-600 text-[10px] sm:text-sm lg:text-base leading-tight">{transcript.original}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              
              {/* Label */}
              <div className="mt-2 sm:mt-3 lg:mt-4 text-left lg:text-left">
                <span className="inline-block bg-indigo-100 text-indigo-700 px-2 sm:px-3 lg:px-4 py-1 sm:py-1.5 lg:py-2 rounded-full text-[10px] sm:text-xs lg:text-sm font-medium">
                  {t('seeInAction.master.title')}
                </span>
              </div>
            </div>

            {/* Listener Session Page - Right Side, Bottom Aligned */}
            <div className="absolute right-0 bottom-0 sm:bottom-0 lg:bottom-0 w-[56%] sm:w-[42%] lg:w-1/2 xl:w-[45%]">
              <div className="bg-gray-50 rounded-lg shadow-lg overflow-hidden border-2 border-gray-300">
                {/* Fixed Top Header - Like SessionListenPage */}
                <div className="bg-white shadow-sm p-2 sm:p-3 lg:p-3 flex justify-between items-center gap-1 sm:gap-2">
                  <h2 className="text-sm sm:text-base lg:text-lg font-bold text-gray-900 truncate min-w-0">Demo</h2>
                  <div className="flex space-x-1 sm:space-x-2 lg:space-x-4 items-center flex-shrink-0">
                    {/* Settings Button */}
                    <button 
                      type="button"
                      className="p-1.5 sm:p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-full transition-colors"
                      title="Settings"
                    >
                      <Settings className="h-3 w-3 sm:h-4 sm:w-4 lg:h-5 lg:w-5" />
                    </button>
                    
                    {/* Language Select Dropdown */}
                    <div className="relative">
                      <button
                        type="button"
                        className="w-full ps-1.5 sm:ps-2 lg:ps-4 pe-5 sm:pe-6 lg:pe-8 py-1 sm:py-1.5 lg:py-2 border border-gray-300 rounded-lg text-left flex items-center justify-between hover:bg-gray-50"
                        style={{ minWidth: '60px' }}
                      >
                        <span className="flex items-center space-x-0.5 sm:space-x-1 lg:space-x-2 text-[9px] sm:text-xs lg:text-sm font-medium text-gray-700">
                          <span>🇩🇪</span>
                          <span className="hidden lg:inline">German</span>
                          <span className="lg:hidden">German</span>
                        </span>
                        <span className="absolute inset-y-0 right-0 flex items-center pr-0.5 sm:pr-1 lg:pr-2 pointer-events-none">
                          <ChevronDown className="h-3 w-3 sm:h-4 sm:w-4 lg:h-5 lg:w-5 text-gray-400" />
                        </span>
                      </button>
                    </div>
                    
                    {/* Pause Button - Red */}
                    <button className="flex items-center justify-center p-1.5 sm:p-2 lg:px-4 lg:py-2 rounded-lg transition-colors bg-red-100 text-red-700 hover:bg-red-200">
                      <Pause className="h-3 w-3 sm:h-4 sm:w-4 lg:h-5 lg:w-5" />
                    </button>
                  </div>
                </div>

                {/* Stenographer Area */}
                <div className="bg-gray-50 p-2 sm:p-3 lg:p-3 overflow-y-auto h-[260px] sm:h-[200px] lg:h-[285px]">
                  {listenerTranscripts.length === 0 ? (
                    <div className="flex items-start justify-center h-full pt-4">
                      <p className="text-gray-500 text-[10px] sm:text-xs lg:text-sm text-center px-1 sm:px-2">Join and wait for translations</p>
                    </div>
                  ) : (
                    <div className="space-y-1 sm:space-y-2 lg:space-y-1.5">
                      {listenerTranscripts.map((transcript) => (
                        <div
                          key={transcript.id}
                          className="bg-white p-2 sm:p-3 lg:p-2.5 rounded-lg shadow-sm animate-fadeIn"
                        >
                          <p className="text-gray-600 text-[10px] sm:text-xs lg:text-sm leading-tight sm:leading-normal lg:leading-tight">{transcript.translated}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              
              {/* Label */}
              <div className="mt-2 sm:mt-3 lg:mt-4 text-right">
                <span className="inline-block bg-emerald-100 text-emerald-700 px-2 sm:px-3 lg:px-4 py-1 sm:py-1.5 lg:py-2 rounded-full text-[10px] sm:text-xs lg:text-sm font-medium">
                  {t('seeInAction.listener.title')}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .animate-fadeIn {
          animation: fadeIn 0.5s ease-out;
        }
      `}</style>
    </section>
  );
};
